import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { CropRect } from '@renderer/utils/kotakCrop'

type CameraKey = 'primary' | 'secondary'

export interface ProjectKotakCrop {
  /**
   * Shared FINAL (on-screen / saved-photo) aspect ratio, w/h. Both panes are locked to this
   * single value — only their position/size differ. Defaults to 1 (square) so untouched
   * booths keep the legacy centered-square behavior.
   */
  ratio: number
  /**
   * Per-camera crop rectangles in unrotated sensor space (see utils/kotakCrop). Undefined
   * until an operator saves one; callers fall back to a centered default fitted to the live
   * sensor so locked mode always has something sane to render.
   */
  primary?: CropRect
  secondary?: CropRect
  /**
   * Operator-set display swap: when true, the physical `secondary` camera is shown/captured on
   * the LEFT box and `primary` on the RIGHT. Purely an app/page-layer role swap (the camera
   * connection is untouched) so the operator can line up screen-left with the physical-left box
   * without guessing which dropdown entry is which. Persisted per project.
   */
  swapped?: boolean
}

/** Left/right physical camera keys for a given swap state (app-layer role mapping). */
export function kotakSides(swapped: boolean | undefined): { leftKey: CameraKey; rightKey: CameraKey } {
  return swapped
    ? { leftKey: 'secondary', rightKey: 'primary' }
    : { leftKey: 'primary', rightKey: 'secondary' }
}

interface KotakCropState {
  /** Keyed by projectId — each booth remembers its own physical-box alignment. */
  byProject: Record<string, ProjectKotakCrop>
  getProjectCrop: (projectId: string) => ProjectKotakCrop
  setRatio: (projectId: string, ratio: number) => void
  setRect: (projectId: string, cameraKey: CameraKey, rect: CropRect) => void
  toggleSwapped: (projectId: string) => void
  resetProject: (projectId: string) => void
}

export const DEFAULT_KOTAK_RATIO = 1

const emptyCrop = (): ProjectKotakCrop => ({ ratio: DEFAULT_KOTAK_RATIO })

export const useKotakCropStore = create<KotakCropState>()(
  persist(
    (set, get) => ({
      byProject: {},

      getProjectCrop: (projectId: string): ProjectKotakCrop => {
        return get().byProject[projectId] ?? emptyCrop()
      },

      setRatio: (projectId: string, ratio: number): void => {
        if (!Number.isFinite(ratio) || ratio <= 0) return
        set((state) => {
          const prev = state.byProject[projectId] ?? emptyCrop()
          return {
            byProject: { ...state.byProject, [projectId]: { ...prev, ratio } }
          }
        })
      },

      setRect: (projectId: string, cameraKey: CameraKey, rect: CropRect): void => {
        set((state) => {
          const prev = state.byProject[projectId] ?? emptyCrop()
          return {
            byProject: { ...state.byProject, [projectId]: { ...prev, [cameraKey]: rect } }
          }
        })
      },

      toggleSwapped: (projectId: string): void => {
        set((state) => {
          const prev = state.byProject[projectId] ?? emptyCrop()
          return {
            byProject: { ...state.byProject, [projectId]: { ...prev, swapped: !prev.swapped } }
          }
        })
      },

      resetProject: (projectId: string): void => {
        set((state) => {
          const next = { ...state.byProject }
          delete next[projectId]
          return { byProject: next }
        })
      }
    }),
    {
      name: 'kotak-crop-storage'
    }
  )
)
